import {
  prefetchCommonUserRetrieveQuery,
  useCommonUserRetrieveSuspense,
} from "@boundless/bl-api-client/common/services"
import { createFileRoute } from "@tanstack/react-router"

import { ChangeEmailDialog } from "@/components/account/my-account/ChangeEmailDialog"
import { DeletionRequestDialog } from "@/components/account/my-account/DeletionRequestDialog"
import { PasswordDialog } from "@/components/account/my-account/PasswordDialog"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { formatDate } from "@/lib/utils"

export const Route = createFileRoute("/_dashboard/settings/my-account")({
  component: RouteComponent,
  loader: async ({ context: { queryClient } }) => {
    await prefetchCommonUserRetrieveQuery(queryClient)
  },
})

function RouteComponent() {
  const { data: user } = useCommonUserRetrieveSuspense()

  return (
    <Card>
      <CardHeader>
        <CardTitle>My Account</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          <div>
            <h3 className="mb-2 text-lg font-medium text-gray-900">
              Account Settings
            </h3>
            <p className="mb-4 text-gray-600">
              Manage your account security and contact information
            </p>
          </div>
          <div className="max-w-2xl space-y-4">
            <div className="flex items-center justify-between rounded-lg bg-gray-50 p-4">
              <div>
                <p className="text-sm font-medium text-gray-900">
                  Email Address
                </p>
                <p className="text-sm text-gray-600">{user.email}</p>
              </div>
              <ChangeEmailDialog />
            </div>

            <div className="flex items-center justify-between rounded-lg bg-gray-50 p-4">
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-gray-900">Password</p>
                  <Badge
                    variant="secondary"
                    className={
                      user.hasUsablePassword
                        ? "border-green-200 bg-green-50 text-green-800"
                        : undefined
                    }
                  >
                    {user.hasUsablePassword ? "Password set" : "Not set"}
                  </Badge>
                </div>
                {!user.hasUsablePassword && (
                  <p className="text-sm text-gray-600">
                    You sign in with an email code. Add a password for another
                    way in.
                  </p>
                )}
              </div>
              <PasswordDialog hasPassword={user.hasUsablePassword} />
            </div>

            <div className="flex items-center justify-between rounded-lg bg-gray-50 p-4">
              <p className="text-sm font-medium text-gray-900">
                Delete Account
              </p>
              <DeletionRequestDialog />
            </div>
          </div>

          <div className="border-t pt-4">
            <h3 className="mb-2 text-lg font-medium text-gray-900">
              Account Information
            </h3>
            <div className="max-w-sm space-y-2 text-sm">
              <div className="flex justify-between gap-4">
                <span className="text-gray-600">Joined</span>
                <span className="font-medium">
                  {formatDate(user.dateJoined)}
                </span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-gray-600">Last Login</span>
                <span className="font-medium">
                  {formatDate(user.lastLogin)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
